import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

const STACK_OVERFLOW_PATTERN = /maximum call stack|stack overflow|too much recursion/i;

/**
 * Application Insights' built-in window.onerror handler frequently loses the
 * originating script and stack for stack-overflow errors (it reports
 * `typeName: "String"` with no url). This listener records the raw ErrorEvent
 * fields — which still carry `filename`/`lineno`/`colno` for same-origin and
 * CORS-enabled scripts, plus `error.stack` when the runtime can allocate it —
 * so the real culprit can be identified, including on iOS/WebKit where these
 * overflows predominantly occur.
 */
export const isStackOverflowError = (message: string, errorName?: string): boolean =>
  STACK_OVERFLOW_PATTERN.test(message) || errorName === 'RangeError';

const getStack = (error: unknown): string | undefined => {
  const stack = (error as { stack?: unknown } | null | undefined)?.stack;
  return typeof stack === 'string' ? stack : undefined;
};

const getName = (error: unknown): string | undefined => {
  const name = (error as { name?: unknown } | null | undefined)?.name;
  return typeof name === 'string' ? name : undefined;
};

export const registerRuntimeErrorCapture = (
  appInsights: ApplicationInsights,
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const onError = (event: ErrorEvent) => {
    // Resource load errors (script/img/link) bubble here with an element target;
    // those are handled by the script-error-context listener instead.
    if (typeof Element !== 'undefined' && event.target instanceof Element) {
      return;
    }

    const message = event.message || '';
    const errorName = getName(event.error);

    appInsights.trackTrace(
      {
        message: `RuntimeError: ${message || 'unknown'}`,
        severityLevel: SeverityLevel.Error,
      },
      {
        capturedVia: 'window-error-listener',
        rawMessage: message,
        filename: event.filename || undefined,
        lineno: event.lineno,
        colno: event.colno,
        errorName,
        rawStack: getStack(event.error),
        stackOverflow: isStackOverflowError(message, errorName),
        userAgent: navigator.userAgent,
        pathname: window.location.pathname,
      },
    );
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      typeof reason === 'object' && reason !== null && 'message' in reason
        ? String((reason as { message: unknown }).message)
        : String(reason);
    const errorName = getName(reason);

    appInsights.trackTrace(
      {
        message: `UnhandledRejection: ${message || 'unknown'}`,
        severityLevel: SeverityLevel.Error,
      },
      {
        capturedVia: 'unhandledrejection-listener',
        rawMessage: message,
        errorName,
        rawStack: getStack(reason),
        stackOverflow: isStackOverflowError(message, errorName),
        userAgent: navigator.userAgent,
        pathname: window.location.pathname,
      },
    );
  };

  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError, true);
    window.removeEventListener('unhandledrejection', onRejection);
  };
};
